// src/api/diagnosticoExpress.js - API para el módulo de Diagnósticos EXPRESS

import { apiFetch } from './index.js';

/**
 * GET /api/DiagnosticoExpress/todos
 * Obtiene TODOS los diagnósticos EXPRESS (sin filtro de fecha)
 * @returns {Promise} Lista de todos los diagnósticos EXPRESS
 */
export const obtenerTodosDiagnosticosExpress = async () => {
  try {
    const response = await apiFetch('/DiagnosticoExpress/todos');
    const data = response.data || response;
    
    return {
      success: true,
      data: data,
      message: 'Diagnósticos EXPRESS obtenidos exitosamente'
    };
  } catch (error) {
    console.error('❌ Error obteniendo todos los diagnósticos EXPRESS:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * GET /api/DiagnosticoExpress/semanal
 * Obtiene diagnósticos EXPRESS de la semana actual
 * @returns {Promise} Lista de diagnósticos EXPRESS de la semana
 */
export const obtenerDiagnosticosExpressSemanal = async () => {
  try {
    const response = await apiFetch('/DiagnosticoExpress/semanal');
    const data = response.data || response;
    
    return {
      success: true,
      data: data,
      message: 'Diagnósticos EXPRESS semanales obtenidos exitosamente'
    };
  } catch (error) {
    console.error('❌ Error obteniendo diagnósticos EXPRESS semanales:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * GET /api/DiagnosticoExpress/mensual
 * Obtiene diagnósticos EXPRESS del mes actual
 * @returns {Promise} Lista de diagnósticos EXPRESS del mes
 */
export const obtenerDiagnosticosExpressMensual = async () => {
  try {
    const response = await apiFetch('/DiagnosticoExpress/mensual');
    const data = response.data || response;
    
    return {
      success: true,
      data: data,
      message: 'Diagnósticos EXPRESS mensuales obtenidos exitosamente'
    };
  } catch (error) {
    console.error('❌ Error obteniendo diagnósticos EXPRESS mensuales:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * POST /api/DiagnosticoExpress/filtrar
 * Obtiene diagnósticos EXPRESS con filtros personalizados
 * @param {Object} filtros - Criterios de filtrado
 * @param {string} filtros.fechaInicio - Fecha de inicio (YYYY-MM-DD)
 * @param {string} filtros.fechaFin - Fecha de fin (YYYY-MM-DD)
 * @param {string} filtros.tipoFiltro - Tipo de filtro (semanal, mensual, personalizado)
 * @param {number} [filtros.idTecnico] - ID del técnico (opcional)
 * @param {string} [filtros.estado] - Estado (opcional)
 * @returns {Promise} Lista de diagnósticos EXPRESS filtrados
 */
export const filtrarDiagnosticosExpress = async (filtros) => {
  try {
    if (!filtros) throw new Error('Los filtros son requeridos');
    if (!filtros.fechaInicio) throw new Error('La fecha de inicio es requerida');
    if (!filtros.fechaFin) throw new Error('La fecha de fin es requerida');
    
    const response = await apiFetch('/DiagnosticoExpress/filtrar', {
      method: 'POST',
      body: filtros
    });
    const data = response.data || response;
    
    return {
      success: true,
      data: data,
      message: 'Diagnósticos EXPRESS filtrados exitosamente'
    };
  } catch (error) {
    console.error('❌ Error filtrando diagnósticos EXPRESS:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * GET /api/DiagnosticoExpress/por-fechas
 * Obtiene diagnósticos EXPRESS por rango de fechas
 * @param {string} fechaInicio - Fecha de inicio (YYYY-MM-DD)
 * @param {string} fechaFin - Fecha de fin (YYYY-MM-DD)
 * @returns {Promise} Lista de diagnósticos EXPRESS en el rango de fechas
 */
export const obtenerDiagnosticosExpressPorFechas = async (fechaInicio, fechaFin) => {
  try {
    if (!fechaInicio) throw new Error('La fecha de inicio es requerida');
    if (!fechaFin) throw new Error('La fecha de fin es requerida');
    
    const response = await apiFetch(`/DiagnosticoExpress/por-fechas?fechaInicio=${fechaInicio}&fechaFin=${fechaFin}`);
    const data = response.data || response;
    
    return {
      success: true,
      data: data,
      message: 'Diagnósticos EXPRESS por fechas obtenidos exitosamente'
    };
  } catch (error) {
    console.error('❌ Error obteniendo diagnósticos EXPRESS por fechas:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

/**
 * GET /api/DiagnosticoExpress/estadisticas
 * Obtiene estadísticas de diagnósticos EXPRESS
 * @returns {Promise} Estadísticas de diagnósticos EXPRESS
 */
export const obtenerEstadisticasDiagnosticosExpress = async () => {
  try {
    const response = await apiFetch('/DiagnosticoExpress/estadisticas');
    const data = response.data || response;
    
    return {
      success: true,
      data: data,
      message: 'Estadísticas de diagnósticos EXPRESS obtenidas exitosamente'
    };
  } catch (error) {
    console.error('❌ Error obteniendo estadísticas de diagnósticos EXPRESS:', error);
    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

// ==================== FUNCIONES DE UTILIDAD ====================

/**
 * Formatea un diagnóstico EXPRESS para mostrar en tabla
 * @param {Object} diagnostico - Diagnóstico EXPRESS
 * @returns {Object} Diagnóstico formateado
 */
export const formatearDiagnosticoExpress = (diagnostico) => {
  if (!diagnostico) return null;
  
  return {
    idRecepcionEquipo: diagnostico.idRecepcionEquipo || diagnostico.IdRecepcionEquipo,
    numeroOrden: diagnostico.numeroOrden || diagnostico.NumeroOrden || 'N/A',
    fechaIngreso: diagnostico.fechaIngreso || diagnostico.FechaIngreso,
    nombreCliente: diagnostico.nombreCliente || diagnostico.NombreCliente || 'Cliente',
    documentoCliente: diagnostico.documentoCliente || diagnostico.DocumentoCliente || 'N/A',
    telefonoCliente: diagnostico.telefonoCliente || diagnostico.TelefonoCliente || '',
    problema: diagnostico.problema || diagnostico.Problema || 'N/A',
    descripcion: diagnostico.descripcion || diagnostico.Descripcion || '',
    diagnostico: diagnostico.diagnostico || diagnostico.Diagnostico || diagnostico.descripcion || '',
    observaciones: diagnostico.observaciones || diagnostico.Observaciones || '',
    nombreEstado: diagnostico.nombreEstado || diagnostico.NombreEstado || 'Pendiente',
    nombreTecnico: diagnostico.nombreTecnico || diagnostico.NombreTecnico || 'No asignado',
    tipoDiagnostico: diagnostico.tipoDiagnostico || diagnostico.TipoDiagnostico || 'EXPRESS',
    montoDiagnostico: diagnostico.montoDiagnostico || diagnostico.MontoDiagnostico || 0,
    actividadesRealizadas: diagnostico.actividadesRealizadas || diagnostico.ActividadesRealizadas || '',
    fechaDiagnostico: diagnostico.fechaDiagnostico || diagnostico.FechaDiagnostico || diagnostico.fechaIngreso,
    prioridad: diagnostico.prioridad || diagnostico.Prioridad || 'Normal',
    presupuesto: diagnostico.presupuesto || diagnostico.Presupuesto || diagnostico.montoDiagnostico || 0,
    diasEstimados: diagnostico.diasEstimados || diagnostico.DiasEstimados || null
  };
};

/**
 * Formatea una fecha para mostrar en la interfaz
 * @param {string|Date} fecha - Fecha a formatear
 * @param {string} formato - Formato de fecha (default: 'dd/MM/yyyy HH:mm')
 * @returns {string} Fecha formateada
 */
export const formatearFechaDiagnostico = (fecha, formato = 'dd/MM/yyyy HH:mm') => {
  if (!fecha) return 'N/A';
  try {
    const date = typeof fecha === 'string' ? new Date(fecha) : fecha;
    if (isNaN(date.getTime())) return 'N/A';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    if (formato === 'dd/MM/yyyy') return `${day}/${month}/${year}`;
    if (formato === 'dd/MM/yyyy HH:mm') return `${day}/${month}/${year} ${hours}:${minutes}`;
    if (formato === 'HH:mm') return `${hours}:${minutes}`;
    if (formato === 'yyyy-MM-dd') return `${year}-${month}-${day}`;
    
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch {
    return 'N/A';
  }
};

/**
 * Obtiene la clase CSS para el estado de un diagnóstico
 * @param {string} estado - Estado del diagnóstico
 * @returns {string} Clase CSS
 */
export const getEstadoDiagnosticoClass = (estado) => {
  if (!estado) return 'bg-gray-100 text-gray-800';
  
  const estadoLower = estado.toLowerCase();
  
  if (['entregado', 'completado', 'finalizado'].includes(estadoLower)) {
    return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
  }
  if (['pendiente', 'en proceso', 'recibido', 'diagnóstico'].includes(estadoLower)) {
    return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
  }
  if (['cancelado', 'anulado'].includes(estadoLower)) {
    return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
  }
  if (['reparado', 'reparación'].includes(estadoLower)) {
    return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
  }
  
  return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
};

/**
 * Obtiene el ícono para el estado de un diagnóstico
 * @param {string} estado - Estado del diagnóstico
 * @returns {string} Nombre del ícono de mdi
 */
export const getEstadoDiagnosticoIcon = (estado) => {
  if (!estado) return 'mdi:circle-outline';
  
  const estadoLower = estado.toLowerCase();
  
  if (['entregado', 'completado', 'finalizado'].includes(estadoLower)) {
    return 'mdi:check-circle';
  }
  if (['pendiente', 'en proceso', 'recibido'].includes(estadoLower)) {
    return 'mdi:clock-outline';
  }
  if (['diagnóstico', 'diagnosticar'].includes(estadoLower)) {
    return 'mdi:stethoscope';
  }
  if (['reparado', 'reparación'].includes(estadoLower)) {
    return 'mdi:tools';
  }
  if (['cancelado', 'anulado'].includes(estadoLower)) {
    return 'mdi:close-circle';
  }
  
  return 'mdi:circle-outline';
};

// ==================== EXPORT DEFAULT ====================

export default {
  obtenerTodosDiagnosticosExpress,
  obtenerDiagnosticosExpressSemanal,
  obtenerDiagnosticosExpressMensual,
  filtrarDiagnosticosExpress,
  obtenerDiagnosticosExpressPorFechas,
  obtenerEstadisticasDiagnosticosExpress,
  formatearDiagnosticoExpress,
  formatearFechaDiagnostico,
  getEstadoDiagnosticoClass,
  getEstadoDiagnosticoIcon
};